import React, {
  Component,
  CSSProperties,
  ReactNode,
  Fragment,
  RefObject,
} from "react";
import "./fillChart.css";
import { FillChartType } from "../data";
import { Locked } from "components/locked/Locked";

export class FillChart extends Component<Props> {
  static defaultProps = {
    leftMarginOffset: 0,
    hideTitle: false,
    center: false,
    width: 138,
    height: 128,

    clickable: false,
    onChartPressed: () => {},
    style: {},

    dashX: 65,
    dashWidth: 142,
    textX: 111.696,
    large: false,
  };

  calculationNumbers = new Set<number>();

  getDashPosition = (index: number) => {
    let offset = this.props.values[index] / 2;
    for (let i = index - 1; i >= 0; i--) {
      offset += this.props.values[i];
    }

    return (offset / 100) * this.props.height - 20;
  };

  svgWidth = (): number => {
    if (!this.props.large) {
      return 207;
    } else if (window.innerWidth >= 1920) {
      return 310.5;
    } else {
      return 275;
    }
  };

  svgHeight = (): number => {
    if (!this.props.large) {
      return 128;
    } else if (window.innerWidth >= 1920) {
      return 192;
    } else {
      return 170;
    }
  };

  animationProps = (
    attribute: string,
    topToBottom: boolean
  ): React.SVGProps<SVGElement> => ({
    attributeName: attribute,
    dur: "1s",
    fill: "freeze",
    from: topToBottom ? 0 : 124,
    to: topToBottom ? 124 : 0,
  });

  render = () => (
    <div
      style={this.props.style}
      onClick={this.props.onChartPressed}
      className={`fillChartCostsContainer${this.props.blur ? " blur" : ""}`}
      ref={this.props.containerRef}
    >
      {this.props.hideTitle ? null : (
        <p className="fillChartLabel">{this.props.label}</p>
      )}
      <div
        className={`fillChartContainer ${
          this.props.clickable ? "fillChartContainerHoverable" : ""
        }`}
        style={{ justifyContent: "center" }}
      >
        <svg
          width={`${this.svgWidth()}px`}
          height={`${this.svgHeight()}px`}
          viewBox="0 0 207 128"
          version="1.1"
          style={{ overflow: "visible" }}
        >
          <defs>
            {this.path()}
            <clipPath id={`clipPath${this.props.icon}`}>
              <rect x="0" y="0" width="207" height="124">
                <animate {...this.animationProps("height", true)} />
                <animate {...this.animationProps("y", false)} />
              </rect>
            </clipPath>
          </defs>
          <g
            id="chart_heart"
            stroke="none"
            strokeWidth="1"
            fill="none"
            fillRule="evenodd"
          >
            {this.props.values[0] > 0 && (
              <g
                id="line_bottom"
                transform={`translate(${
                  this.props.dashX
                }, ${this.getDashPosition(0)})`}
              >
                <line
                  x1="0"
                  y1="20"
                  x2={this.props.dashWidth}
                  y2="20"
                  id="Path"
                  stroke="#7AD077"
                  strokeDasharray="5"
                ></line>
                <text
                  id="40%"
                  fontFamily="SourceSansPro-Bold, Source Sans Pro"
                  fontSize="16"
                  fontWeight="bold"
                  fill="#233142"
                >
                  <tspan x={this.props.textX} y="16">
                    {this.props.values[0]}%
                  </tspan>
                </text>
              </g>
            )}
            {this.props.values[1] > 0 && (
              <g
                id="line_middle"
                transform={`translate(${
                  this.props.dashX
                }, ${this.getDashPosition(1)})`}
              >
                <line
                  x1="0"
                  y1="20"
                  x2={this.props.dashWidth}
                  y2="20"
                  id="Path"
                  stroke="#7AD077"
                  strokeDasharray="5"
                ></line>
                <text
                  id="40%"
                  fontFamily="SourceSansPro-Bold, Source Sans Pro"
                  fontSize="16"
                  fontWeight="bold"
                  fill="#233142"
                >
                  <tspan x={this.props.textX} y="16">
                    {this.props.values[1]}%
                  </tspan>
                </text>
              </g>
            )}
            {this.props.values[2] && (
              <g
                id="line_top"
                transform={`translate(${
                  this.props.dashX
                }, ${this.getDashPosition(2)})`}
              >
                <line
                  x1="0"
                  y1="20"
                  x2={this.props.dashWidth}
                  y2="20"
                  id="Path"
                  stroke="#7AD077"
                  strokeDasharray="5"
                ></line>
                <text
                  id="40%"
                  fontFamily="SourceSansPro-Bold, Source Sans Pro"
                  fontSize="16"
                  fontWeight="bold"
                  fill="#233142"
                >
                  <tspan x={this.props.textX} y="16">
                    {this.props.values[2]}%
                  </tspan>
                </text>
              </g>
            )}
            <g id="shape">
              <mask id={`mask-${this.props.icon}`} fill="white">
                <use href={`#path-${this.props.icon}`}></use>
              </mask>
              <g id="Mask"></g>

              <g
                id="rectangles"
                mask={`url(#mask-${this.props.icon})`}
                clipPath={`url(#clipPath${this.props.icon})`}
              >
                <rect width="207" height="124" fill="white" />
                <rect x="0" width="207" {...this.backgroundHeight(0)}></rect>
                <rect x="0" width="207" {...this.backgroundHeight(1)}></rect>
                <rect x="0" width="207" {...this.backgroundHeight(2)}></rect>
              </g>
            </g>
            {this.extraPath()}
          </g>
        </svg>
      </div>

      <div className="fillChartLegendContainer">
        <div className="fillChartLegendEntry">
          <span
            className="fillChartCircle"
            style={{ backgroundColor: this.props.colors[0] }}
          />
          <span>Healthy</span>
        </div>
        <div className="fillChartLegendEntry">
          <span
            className="fillChartCircle"
            style={{ backgroundColor: this.props.colors[1] }}
          />
          <span>At Risk</span>
        </div>
        <div className="fillChartLegendEntry">
          <span
            className="fillChartCircle"
            style={{ backgroundColor: this.props.colors[2] }}
          />
          <span>Struggling</span>
        </div>
      </div>
      {this.props.clickable && (
        <p
          className="viewMore centerTopMargin center"
          onClick={this.props.onChartPressed}
        >
          See Details
        </p>
      )}

      {this.props.locked && <Locked />}
    </div>
  );

  backgroundHeight = (index: number) => {
    const firstTwoHeights = [
      Math.floor((this.props.values[0] / 100) * this.props.height),
      Math.floor((this.props.values[1] / 100) * this.props.height),
    ];
    const height =
      index < 2
        ? firstTwoHeights[index]
        : this.props.height - firstTwoHeights[0] - firstTwoHeights[1];

    let top = 0;
    if (index === 1) {
      top = firstTwoHeights[0];
    } else if (index === 2) {
      top = firstTwoHeights[0] + firstTwoHeights[1];
    }

    return {
      y: top,
      height,
      fill: this.props.colors[index],
      stroke: this.props.colors[index],
      strokeWidth: 1,
    };
  };

  delay = (index: number): CSSProperties => {
    const reversed = [...this.props.values].reverse();
    let previousAnimationDuration = 0;
    if (index > 0) {
      for (let i = 0; i < index; i++) {
        previousAnimationDuration += (700 * reversed[i]) / 100;
      }
    }
    const animationDuration = (700 * reversed[index]) / 100;

    return {
      animationDuration: `${animationDuration}ms`,
      animationDelay: `${previousAnimationDuration}ms`,
    };
  };

  dashStyle = (top: number, height: number): CSSProperties => ({
    top: `${top}%`,
    left: 0,
    right: 0,
    height: `${height}%`,
    borderBottom: "1px dashed #7AD077",
    position: "absolute",
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "flex-end",
  });

  path = (): ReactNode => {
    switch (this.props.icon) {
      case "cdv":
        return (
          <path
            d="M64.9963,123.999997 C66.8205,123.999997 68.5655,123.166 69.8277,121.692 L120.403,62.6317 C130.379,51.4636 132.849,34.3998 126.532,20.2767 C121.819,9.7296 112.865,2.36132 102.466,0.47356 C92.0676,-1.414194 81.4922,2.40865 74.0434,10.748 L64.9963,20.876 L55.9493,10.748 C48.5005,2.40865 37.925,-1.414194 27.5263,0.47356 C17.1276,2.36132 8.1733,9.7296 3.46029,20.2767 C-2.8484,34.4037 -0.37394,51.4649 9.6032,62.6317 L60.165,121.692 C61.4271,123.166 63.1722,123.999997 64.9963,123.999997 Z"
            id={`path-${this.props.icon}`}
          ></path>
        );
      case "obesity":
        return (
          <path
            d="M33.4925373,31.4920635 C41.9004836,31.4920635 48.7164179,24.4422729 48.7164179,15.7460317 C48.7164179,7.04973853 41.9004836,-7.33137509e-16 33.4925373,-7.33137509e-16 C25.0846925,-7.33137509e-16 18.2686567,7.04973853 18.2686567,15.7460317 C18.2686567,24.4422729 25.0846925,31.4920635 33.4925373,31.4920635 Z M34,32.5079365 C48.7069576,32.5079365 68,45.757127 68,62.1009561 L68,83.9050477 C68,85.8718975 66.4133333,87.466372 64.4556545,87.466372 C62.5943091,87.466372 61.0500909,86.019626 60.9199636,84.1539191 L59.8116667,68.2595905 C59.7826121,67.8435281 59.4381818,67.5209477 59.0230727,67.5209477 C58.5098788,67.5209477 58.116303,68.0044042 58.1866727,68.5151909 C58.4822667,70.6618616 58.8805818,76.2805155 56.9440242,85.3345051 C55.8469576,90.4635947 53.5383576,93.7990299 51.1394,95.9583305 L46.5918485,127.114871 C46.5176667,127.623173 46.083703,128 45.5724667,128 L22.4261939,128 C21.9149576,128 21.4809939,127.623173 21.4068121,127.114871 L16.8590545,95.9569847 C14.4607152,93.7975806 12.1527333,90.4624559 11.0559758,85.3345051 C9.11941818,76.2805155 9.51773333,70.6618616 9.81332727,68.5151909 C9.88369697,68.0044042 9.49012121,67.5209477 8.97692727,67.5209477 C8.56181818,67.5209477 8.21738788,67.8435281 8.18833333,68.2595905 L7.08003636,84.1539191 C6.94990909,86.019626 5.40569091,87.466372 3.54434545,87.466372 C1.58687273,87.466372 0,85.8718975 0,83.9050477 L0,62.1009561 C0,45.757127 19.2930424,32.5079365 34,32.5079365 Z"
            id={`path-${this.props.icon}`}
          ></path>
        );
      case "msd":
        return (
          <path
            d="M72,32 C80.8365312,32 88,24.8365419 88,16 C88,7.16345806 80.8365312,0 72,0 C63.1634688,0 56,7.16345806 56,16 C56,24.8365419 63.1634688,32 72,32 Z M54.5072521,24.8980429 C45.7819992,22.3314843 27.6093314,22.3677074 24.6677487,25.0074365 C21.7499092,27.6260528 18.0817446,39.7905822 17.2117786,47.1825689 C16.7965302,50.708314 16.4436719,55.137876 21.8121964,56.0555611 C27.1808238,56.9731428 28.959197,52.1666541 29.9121099,49.2499739 L32.3185977,38.7818219 C32.4230265,38.3276883 32.8232684,38.0062345 33.2844614,38.0062345 L36.9761635,38.0062345 C37.7693497,38.0062345 38.2625085,38.9075676 37.8894017,39.6152626 C33.9186402,47.1460354 29.8574286,64.702211 32.4969284,87.7111981 C33.9881224,100.710928 33.6153239,108.357407 31.7512286,114.474963 C31.1298977,117.661557 31.4530515,124.493225 37.716313,126.328182 C43.9796773,128.163138 47.0366867,120.720853 47.7823865,116.769435 C48.5279835,108.48574 49.7210004,91.2287671 48.5279835,88.4759185 C50.3920788,92.4268187 55.1536623,104.39771 53.3745696,120.210625 C52.2561741,130.151266 70.0270583,133.211597 68.2872292,113.32928 C68.25588,112.970154 68.2234002,112.598609 68.1898925,112.212575 L68.1898925,112.212575 L68.1898925,112.212575 C67.3009114,101.995604 65.6278921,82.7685107 61.6705954,75.2689932 C61.6076914,75.1497676 61.569764,75.0276442 61.5566076,74.8932049 C61.2331456,71.5923533 60.8206723,65.08483 61.3030389,61.2815128 C61.3932834,60.570713 62.1942812,60.2844474 62.7547637,60.7230568 C66.7930544,63.8830525 79.9243601,74.1250692 81.708592,75.0937771 C83.8212196,76.240806 88.686718,78.5041259 91.3714428,74.527766 C94.0551398,70.5514062 87.4142489,65.4902177 85.1776635,63.8333752 C84.9253281,63.6464642 84.6479134,63.4448571 84.3481945,63.2270013 C81.9905292,61.5136509 78.2590495,58.8018896 74.6251121,54.4471541 C70.460294,49.4562383 68.9135964,38.5962563 68.5775947,35.8130839 C68.5289778,35.4104905 68.2538243,35.0759965 67.8725975,34.9497333 C66.4815153,34.488976 63.0940546,33.2942362 60.8309508,31.8896097 C58.2905351,30.3129754 55.7787962,26.4767471 55.0659873,25.3335475 C54.9352458,25.1238678 54.7427308,24.9672807 54.5072521,24.8980429 Z M0.409808772,59.6753834 C0.232066355,59.681489 0.0761935796,59.7617182 0.0209005236,59.8754744 C-0.0344956911,59.9892306 0.0226542212,60.1117703 0.163568987,60.1813951 L11.822048,66.9143282 C12.0123757,67.0130883 12.2743988,67.0276559 12.4941268,66.9518185 C12.713958,66.8758739 12.8518811,66.723342 12.8480642,66.5602057 L12.7273686,62.6210483 L21.5884934,62.3259463 C21.7665453,62.3202692 21.9231402,62.2402542 21.9788459,62.1262837 C22.0346548,62.0124204 21.977608,61.8895594 21.8363838,61.8198275 L10.1779048,55.0868944 C9.98798966,54.9871702 9.72524449,54.9719599 9.50479438,55.0481187 C9.28444742,55.1241704 9.14673057,55.2775592 9.15199167,55.4409097 L9.27258417,59.3801742 L0.409808772,59.6753834 Z M3.00645493,84.8418736 C2.97784511,84.7200213 3.04608484,84.5626998 3.17652443,84.4493686 L9.69161672,78.8025168 L7.16229652,75.8291721 C7.05718191,75.7059351 7.06640063,75.503132 7.18582015,75.3094891 C7.30534563,75.1159528 7.51324367,74.9670459 7.71849265,74.9279552 L20.6963319,73.0086753 C20.8461626,72.976295 20.9653701,73.036369 20.9936621,73.1586473 C21.02206,73.2808191 20.9532904,73.4384602 20.8222151,73.5516848 L14.3083943,79.1974715 L16.8377145,82.1708163 C16.9415576,82.2945858 16.9317031,82.4969628 16.8126015,82.6900731 C16.6934998,82.8830769 16.4864495,83.0319838 16.2815184,83.0720331 L3.3036792,84.991313 C3.15416639,85.0236934 3.03506476,84.9637259 3.00645493,84.8418736 Z"
            id={`path-${this.props.icon}`}
          ></path>
        );
      case "stress":
      default:
        return (
          <path
            d="M66.9907087,30.8602594 C67.3297287,30.8602594 67.666267,30.8501108 68,30.8301242 C67.8279004,31.0269871 67.6811572,31.2496358 67.5666759,31.4954814 L55.222117,58 L50,37.2986393 L57.7103859,28.0014498 L57.7115728,28 C60.3180995,29.7993124 63.5241155,30.8602594 66.9907087,30.8602594 Z M82,15.2125122 C82,22.0313908 77.450406,27.8023869 71.1810592,29.7347409 C72.4531143,30.4435534 72.9927932,32.0090963 72.3851282,33.361294 L58.1843541,64.9611821 C57.8049582,65.8053505 57.0626892,66.3788071 56.2210722,66.5802183 C56.0087999,66.7638333 55.7496951,66.9004453 55.4562816,66.9695365 C51.5799181,67.8821679 47.6775598,65.4999885 46.740173,61.6487845 L42.888015,45.8220938 L25.9435926,66.997487 L49.5855899,66.997487 C54.6069819,66.997487 58.986716,69.7303564 61.3033991,73.7823435 C61.5574523,74.1315679 61.7602529,74.5246547 61.8984351,74.9533339 C62.5573527,76.4258131 62.9571655,78.0383588 63.0403064,79.7336046 L73.0659361,122.220166 C73.5975114,124.471909 72.1932728,126.726794 69.9301576,127.256492 L67.237341,127.887734 C64.9743311,128.417433 62.7090058,127.022001 62.1777463,124.770258 L52.4975195,83.7468588 L12.5407603,83.7468588 L8.02189682,83.7468588 L8.00758396,83.7468588 L7.27847022,83.7468588 C6.90907223,83.7468588 6.55072461,83.6995418 6.20932089,83.6106654 C3.99556402,83.2647909 1.98860687,82.2317734 1.10005212,80.1824878 C0.309372043,78.3587952 -0.26819381,75.8011662 0.128461748,72.43203 C1.05574541,64.555952 2.85537662,51.7498011 17.2067515,39.5540601 C28.0640002,30.3276687 43.6676447,21.5790531 51.2691396,17.5437107 C51.2768223,17.539628 51.2845049,17.5355454 51.2921875,17.5315674 C51.3018698,17.5264379 51.311552,17.5214131 51.3212342,17.516493 C51.2054685,16.7650743 51.1454808,15.9957547 51.1454808,15.2125122 C51.1454808,6.81085985 58.0525916,0 66.5729509,0 C75.0929944,0 82,6.81085985 82,15.2125122 Z"
            id={`path-${this.props.icon}`}
          ></path>
        );
    }
  };

  extraPath = (): ReactNode => {
    switch (this.props.icon) {
      case "cdv":
        return (
          <path
            d="M101,-1 L100.999728,0.0103696238 C91.8359464,-0.230205166 82.8970203,3.72263169 76.3216899,11.0947127 L66.9962135,21.5494221 L57.6708401,11.0947127 C50.1423692,2.65399646 39.5152983,-1.30434139 28.9996822,0.381689549 L29,-1 L101,-1 Z"
            id="overlay"
            fill="#FFFFFF"
          ></path>
        );
      case "obesity":
        return (
          <path
            d="M40,30 L40.0005747,33.1753911 C37.957156,32.7381753 35.9387305,32.5079365 34,32.5079365 C31.4308488,32.5079365 28.7217467,32.9122544 25.999991,33.6667574 L26,30 L27.0164442,30.0004372 C28.9815965,30.9572173 31.1766841,31.4920635 33.4925373,31.4920635 C35.8084185,31.4920635 38.0035186,30.9572173 39.9686745,30.0004372 L40,30 Z"
            id="Combined-Shape"
            fill="#FFFFFF"
          ></path>
        );
      case "msd":
        return (
          <Fragment>
            {" "}
            <path
              d="M22.0008532,12.4961153 L22.0001216,16 C21.9575665,15.9809458 21.9158208,15.9640087 21.8727191,15.9497333 C20.481637,15.488976 17.0941762,14.2942362 14.8310724,12.8896097 C12.2906567,11.3129754 9.77891777,7.47674708 9.06610894,6.33354751 L9.00012162,6.239 L9.00012162,0 L10.2810195,0.000552077074 C11.4371655,6.09416465 16.0489146,10.9644861 22.0008532,12.4961153 Z"
              id="Combined-Shape"
            ></path>
            <path
              d="M2.7703118,69.9997451 C4.77016332,74.4108636 9.08113645,86.0434145 7.3746912,101.210625 C7.20860528,102.686848 7.45909149,104.011336 8.00038959,105.131486 L8.00012162,107 L0.000121617543,107 L-1.88737914e-15,102.868074 C0.930774924,101.117226 1.51045716,99.2110164 1.78250809,97.7694352 C2.49052908,89.9032155 3.6020089,73.945383 2.69066127,69.9996373 L2.7703118,69.9997451 Z"
              id="Combined-Shape"
            ></path>
          </Fragment>
        );
    }
  };
}

interface Props {
  colors: string[];
  values: number[];
  width: number;
  height: number;
  label: string;
  icon: FillChartType;
  leftMarginOffset: number;

  hideTitle: boolean;
  center: boolean;

  clickable: boolean;
  onChartPressed: () => void;
  style: CSSProperties;

  dashX: number;
  dashWidth: number;
  textX: number;
  large: boolean;
  containerRef?: RefObject<HTMLDivElement>;
  blur?: boolean;
  locked?: boolean;
}
